import { HeaderShownContext } from 'expo-router/react-navigation';
import React, { useEffect } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import {
  AnalyticsProps,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import theme from 'shared/styles/theme';
import { logError } from 'shared/utils/sentry/sentry';

const NAV_BAR_HEIGHT = 48;
const DEFAULT_TOP_SPACING = 112;

/** Stable testID prefix on every ErrorScreen root — lets e2e distinguish an inline error screen from a broken locator. */
export const ERROR_SCREEN_TEST_ID_PREFIX = 'inline-error-screen';

export const enum ErrorScreenType {
  Error,
  Warning,
}

export interface ErrorScreenProps extends Omit<AnalyticsProps, 'key'> {
  screenType: ErrorScreenType;
  messageHeadline?: string;
  messageBody?: string;
  content?: React.ReactNode;
  primaryButton?: {
    title: string;
    onPress: () => void;
    isLoading?: boolean;
    analytics?: PressableAnalyticsProps;
  };
  secondaryButton?: {
    title: string;
    onPress: () => void;
    isLoading?: boolean;
    analytics?: PressableAnalyticsProps;
  };
  screenAnalyticsEventKey?: string | undefined;
  screenCustomAttributes?: Record<string, string> | null;
  insetBottomSafeArea?: boolean;
  testID?: string;
  error?: unknown;
}

export const ErrorScreen = ({
  screenType,
  messageHeadline,
  messageBody,
  content,
  primaryButton,
  secondaryButton,
  screenAnalyticsEventKey,
  customAttributes = null,
  insetBottomSafeArea = true,
  testID = ERROR_SCREEN_TEST_ID_PREFIX,
  error,
}: ErrorScreenProps) => {
  const isHeaderShown = React.useContext(HeaderShownContext);
  const isHeaderHidden = isHeaderShown === false;
  const insets = useSafeAreaInsets();

  useViewDidFocusScreenAnalyticsEvent(
    screenAnalyticsEventKey
      ? {
          key: screenAnalyticsEventKey,
          customAttributes,
        }
      : null,
  );

  useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error]);

  // Calculate spacing: 112px from nav bar (or phantom nav bar if hidden)
  const topSpacing = React.useMemo(() => {
    // When header is hidden, add safe area + nav bar height to maintain same absolute position
    const headerOffset = isHeaderHidden ? insets.top + NAV_BAR_HEIGHT : 0;

    return DEFAULT_TOP_SPACING + headerOffset;
  }, [isHeaderHidden, insets.top]);

  const bottomPadding = insetBottomSafeArea ? insets.bottom : 0;

  return (
    <View
      testID={testID}
      style={[styles.container, { paddingBottom: bottomPadding }]}
    >
      <ScrollView
        contentInsetAdjustmentBehavior="never"
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingTop: topSpacing },
        ]}
      >
        <View style={styles.contentGroup}>
          <View style={styles.iconContainer}>
            {screenType === ErrorScreenType.Error ? (
              <Icon
                iconKey="ERROR"
                iconSize={100}
                iconAccessibilityLabel="error"
              />
            ) : (
              <Icon
                iconKey="WARNING"
                iconSize={100}
                iconAccessibilityLabel="warning"
              />
            )}
          </View>
          <View style={styles.textContainer}>
            {messageHeadline ? (
              <TitleLgText style={styles.messageHeadline}>
                {messageHeadline}
              </TitleLgText>
            ) : null}
            {messageBody ? (
              <BodyBaseText style={styles.messageBody}>
                {messageBody}
              </BodyBaseText>
            ) : null}
          </View>
          {content ? (
            <View style={styles.contentContainer}>{content}</View>
          ) : null}
        </View>
      </ScrollView>

      <View style={styles.buttonContainer}>
        {primaryButton ? (
          <Button
            label={primaryButton.title}
            onPress={primaryButton.onPress}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
            isLoading={primaryButton.isLoading}
            analyticsId={primaryButton.analytics?.analyticsId}
            customAttributes={primaryButton.analytics?.customAttributes}
          />
        ) : null}
        {secondaryButton ? (
          <Button
            label={secondaryButton.title}
            onPress={secondaryButton.onPress}
            size="LARGE"
            type="SECONDARY"
            width="FULL"
            isLoading={secondaryButton.isLoading}
            analyticsId={secondaryButton.analytics?.analyticsId}
            customAttributes={secondaryButton.analytics?.customAttributes}
          />
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    borderColor: theme.color.border.primary,
    borderTopWidth: 1,
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    height: '100%',
    width: '100%',
  },
  contentContainer: {
    marginTop: theme.spacing.m,
  },
  contentGroup: {
    alignItems: 'center',
    flexShrink: 1,
    gap: theme.spacing.m,
  },
  iconContainer: {
    alignItems: 'center',
  },
  messageBody: {
    textAlign: 'center',
  },
  messageHeadline: {
    textAlign: 'center',
  },
  scrollContent: {
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  scrollView: {
    flex: 1,
  },
  textContainer: {
    alignItems: 'center',
    gap: theme.spacing.m,
  },
});

export default ErrorScreen;
